import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import GrillaEffect from 'views/Grillas/GrillaEffect';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { useHistory } from 'react-router-dom';

const ConciliarOps = () => {
  const [registros, setRegistros] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [validarPalabras, setValidarPalabras] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const history = useHistory();

  const showToast = (title, description = '', severity = 'info') => {
    setSnackbar({ open: true, message: description ? `${title}: ${description}` : title, severity });
  };

  useEffect(() => {
    if (registros.length > 0) { handleSearch(); }
  }, [validarPalabras]);

  const handleSearch = async () => {
    setIsLoading(true);
    try {
      const baseURL = `${process.env.REACT_APP_API_URL}/tesoreriacomprobantes/items/extbc-lops-coincidencias?filters=${encodeURIComponent(JSON.stringify(filters))}&validar_palabras=${validarPalabras}`;
      const response = await fetch(baseURL, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('token'),
        },
      });
      if (response.ok) {
        const data = await response.json();
        setRegistros(data.data);
      } else {
        throw new Error(`Error: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error fetching registros:', error);
      showToast('Error al buscar coincidencias', 'No se pudo buscar coincidencias.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConciliar = async (id_registro, registroExtbc) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/tesoreriacomprobantes/items/registros/conciliar`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('token'),
        },
        body: JSON.stringify({ idRegistroLops: id_registro, idRegistroExtbc: registroExtbc }),
      });
      if (response.ok) {
        showToast('Registro conciliado', `Los registros con ID ${id_registro} e ${registroExtbc} fueron conciliados.`, 'success');
        handleSearch();
      } else {
        throw new Error(`Error: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error conciliando registros:', error);
      showToast('Error al conciliar registros', 'No se pudo conciliar los registros.', 'error');
    }
  };

  const handleConciliarMultiple = async () => {
    if (selectedRows.length === 0) return;
    setIsLoading(true);
    try {
      for (const row of selectedRows) {
        await handleConciliar(row.id_registro, row.registroExtbc);
      }
      setSelectedRows([]);
      await handleSearch();
    } catch (error) {
      // toast ya manejado en handleConciliar
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearFilters = () => {
    setFilters([]);
    showToast('Filtros limpiados', 'Se han eliminado todos los filtros aplicados.', 'info');
  };

  const handleSwitchChange = () => {
    const nuevoValor = !validarPalabras;
    setValidarPalabras(nuevoValor);
    showToast(
      nuevoValor ? 'Validación de palabras activada' : 'Validación de palabras desactivada',
      nuevoValor ? 'Se mostrarán solo coincidencias con palabras en común' : 'Se mostrarán todas las coincidencias por importe y fecha',
      'info'
    );
  };

  const NoResultsMessage = () => (
    <Stack spacing={2} sx={{ p: 4, textAlign: 'center', alignItems: 'center' }}>
      <SearchOffIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
      <Typography variant="h6" color="text.secondary">
        No se encontraron coincidencias con los filtros aplicados
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Intenta ajustar los criterios de búsqueda o limpiar los filtros
      </Typography>
      <Button startIcon={<ClearAllIcon />} variant="outlined" onClick={handleClearFilters}>
        Limpiar todos los filtros
      </Button>
    </Stack>
  );

  const customTopToolbar = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <IconButton
        color="primary"
        disabled={selectedRows.length === 0}
        onClick={handleConciliarMultiple}
        title="Conciliar Seleccionados"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, borderRadius: 1 }}
      >
        <DoneAllIcon /> Conciliar Seleccionados
      </IconButton>
      <IconButton
        color="secondary"
        disabled={filters.length === 0}
        onClick={handleClearFilters}
        title="Limpiar Filtros"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, borderRadius: 1 }}
      >
        <ClearAllIcon /> Limpiar Filtros
      </IconButton>
      <FormControlLabel
        control={
          <Switch
            checked={validarPalabras}
            onChange={handleSwitchChange}
            color={validarPalabras ? 'success' : 'warning'}
          />
        }
        label={validarPalabras ? 'Con Validación' : 'Sin Validación'}
        sx={{ ml: 1 }}
      />
      <Button
        variant="contained"
        color="secondary"
        size="small"
        onClick={() => history.push('/admin/registros-conciliados')}
      >
        Ver registros conciliados
      </Button>
      <Typography variant="body2" color="text.secondary">
        {selectedRows.length > 0 ? `${selectedRows.length} seleccionados` : ''}
        {filters.length > 0 && ` | ${filters.length} filtros aplicados`}
      </Typography>
    </Box>
  );

  const onRowSelectionChange = (rowSelection, table) => {
    const selected = Object.keys(rowSelection)
      .filter(rowId => rowSelection[rowId])
      .map(rowId => table.getRowModel().rows.find(r => r.id === rowId)?.original)
      .filter(Boolean);
    setSelectedRows(selected);
  };

  const columnNames = {
    num_comprobante: 'OP N°',
    fecha_movimiento: 'Fecha OP',
    razonSocial: 'Razón Social',
    debito: 'Importe OP',
    fecha_valor: 'Fecha Reg. EXTBCO',
    descripcion: 'Descripcion EXTBCO',
    importe: 'Importe EXTBCO',
    descripcionConcepto: 'Cta Bco',
    accionesCO: 'Acciones',
  };

  const colsGrillaHidden = {
    id_registro: false,
    id_comprobante: false,
    registroExtbc: false,
    id_registro_conciliado: false,
    debitoExtbc: false,
  };

  return (
    <Box sx={{ p: 2, pt: '120px' }}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
        {registros.length === 0 && !isLoading && filters.length > 0 ? (
          <NoResultsMessage />
        ) : (
          <GrillaEffect
            data={registros}
            updateData={setRegistros}
            filters={filters}
            updateFilters={setFilters}
            colsHeaders={columnNames}
            hiddenColumns={colsGrillaHidden}
            method="GET"
            baseURI={`${process.env.REACT_APP_API_URL}/tesoreriacomprobantes/items/extbc-lops-coincidencias`}
            handleConciliar={handleConciliar}
            customTopToolbar={customTopToolbar}
            onRowSelectionChange={onRowSelectionChange}
            isLoading={isLoading}
            additionalParams={{ validar_palabras: validarPalabras }}
            rowSelectionEnabled={true}
          />
        )}
      </LocalizationProvider>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConciliarOps;
